import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Hash, Send, Users, MessageSquare, Search } from "lucide-react";

function formatTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
}

export default function Chat() {
  const { user } = useAuth();
  const { channelId, peerId } = useParams();
  const navigate = useNavigate();

  const [channels, setChannels] = useState([]);
  const [dms, setDms] = useState([]);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [error, setError] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const scrollRef = useRef(null);

  const activeMode = peerId ? "dm" : "channel";
  const activeId = peerId || channelId;

  // Load channels & dms
  useEffect(() => {
    api.get("/chat/channels").then((r) => {
      setChannels(r.data);
      if (!channelId && !peerId && r.data.length > 0) {
        navigate(`/chat/${r.data[0].id}`, { replace: true });
      }
    });
    api.get("/chat/dms").then((r) => setDms(r.data));
  }, []); // eslint-disable-line

  const loadMessages = useCallback(async () => {
    if (!activeId) return;
    try {
      const params = peerId ? { with_user: peerId } : { channel_id: channelId };
      const { data } = await api.get("/chat/messages", { params });
      setMessages(data);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, [activeId, channelId, peerId]);

  useEffect(() => {
    loadMessages();
    const i = setInterval(loadMessages, 4000);
    return () => clearInterval(i);
  }, [loadMessages]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    const body = peerId
      ? { recipient_id: peerId, content: text.trim() }
      : { channel_id: channelId, content: text.trim() };
    try {
      await api.post("/chat/messages", body);
      setText("");
      await loadMessages();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const doSearch = async (e) => {
    e.preventDefault();
    if (!searchQ.trim()) return;
    const { data } = await api.get("/users", { params: { q: searchQ } });
    setSearchResults(data.filter((u) => u.user_id !== user.user_id));
  };

  const activeChannel = channels.find((c) => c.id === channelId);
  const activePeer = dms.find((d) => d.peer_id === peerId);

  return (
    <div className="h-[calc(100vh-12rem)] grid grid-cols-12 gap-4 fade-in" data-testid="chat-page">
      {/* Sidebar: Channels + DMs */}
      <aside className="col-span-12 md:col-span-4 lg:col-span-3 card-solid flex flex-col overflow-hidden">
        <div className="p-4 border-b border-white/10">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2">Kanäle</div>
          <div className="space-y-1">
            {channels.map((c) => (
              <Link
                key={c.id}
                to={`/chat/${c.id}`}
                data-testid={`channel-link-${c.name}`}
                className={`flex items-center gap-2 px-2 py-1.5 rounded-sm text-sm transition-colors ${
                  channelId === c.id ? "bg-primary/10 text-primary" : "text-white/70 hover:bg-white/5"
                }`}
              >
                <Hash className="w-3.5 h-3.5" />
                {c.name}
              </Link>
            ))}
          </div>
        </div>
        <div className="p-4 flex-1 overflow-y-auto">
          <div className="flex items-center justify-between mb-2">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">Direkt</div>
            <button
              onClick={() => setShowSearch((s) => !s)}
              className="text-white/50 hover:text-primary"
              data-testid="search-users-toggle"
            >
              <Search className="w-3.5 h-3.5" />
            </button>
          </div>
          {showSearch && (
            <form onSubmit={doSearch} className="mb-3">
              <input
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                placeholder="Mitglied suchen…"
                className="input text-xs py-1.5"
                data-testid="search-users-input"
              />
              {searchResults.length > 0 && (
                <div className="mt-2 space-y-1">
                  {searchResults.map((u) => (
                    <button
                      key={u.user_id}
                      type="button"
                      onClick={() => {
                        navigate(`/dm/${u.user_id}`);
                        setShowSearch(false);
                        setSearchQ("");
                        setSearchResults([]);
                      }}
                      data-testid={`start-dm-${u.user_id}`}
                      className="flex items-center gap-2 w-full p-1.5 text-left text-sm hover:bg-white/5 rounded-sm"
                    >
                      <div className="w-6 h-6 bg-primary/10 border border-primary/30 text-primary flex items-center justify-center font-mono text-[10px] rounded-sm">
                        {u.name?.[0]?.toUpperCase()}
                      </div>
                      <span className="truncate">{u.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </form>
          )}
          <div className="space-y-1">
            {dms.length === 0 && <div className="text-xs text-white/40 font-mono uppercase tracking-[0.2em]">Keine DMs</div>}
            {dms.map((d) => (
              <Link
                key={d.peer_id}
                to={`/dm/${d.peer_id}`}
                data-testid={`dm-link-${d.peer_id}`}
                className={`flex items-center gap-2 px-2 py-1.5 rounded-sm text-sm transition-colors ${
                  peerId === d.peer_id ? "bg-primary/10 text-primary" : "text-white/70 hover:bg-white/5"
                }`}
              >
                <div className="w-6 h-6 bg-primary/10 border border-primary/30 text-primary flex items-center justify-center font-mono text-[10px] rounded-sm">
                  {d.peer_name?.[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="truncate text-sm">{d.peer_name}</div>
                  <div className="truncate text-[10px] text-white/40 font-mono">{d.last_message}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </aside>

      {/* Messages */}
      <section className="col-span-12 md:col-span-8 lg:col-span-9 card-solid flex flex-col overflow-hidden">
        <header className="px-5 py-4 border-b border-white/10 flex items-center gap-3">
          {activeMode === "channel" ? (
            <>
              <Hash className="w-4 h-4 text-primary" />
              <div>
                <div className="font-display text-lg tracking-tight">{activeChannel?.name || "—"}</div>
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/50">
                  {activeChannel?.description || "Vereinskanal"}
                </div>
              </div>
            </>
          ) : (
            <>
              <Users className="w-4 h-4 text-primary" />
              <div>
                <div className="font-display text-lg tracking-tight">{activePeer?.peer_name || "Direktnachricht"}</div>
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/50">Privat</div>
              </div>
            </>
          )}
        </header>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4">
          {messages.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-white/40 font-mono text-xs uppercase tracking-[0.2em]">
              <MessageSquare className="w-8 h-8 mb-2" strokeWidth={1.25} />
              Sag Hallo 👋
            </div>
          )}
          {messages.map((m) => {
            const mine = m.sender_id === user.user_id;
            return (
              <div key={m.id} className={`flex gap-3 ${mine ? "flex-row-reverse" : ""}`} data-testid={`msg-${m.id}`}>
                {m.sender_picture ? (
                  <img src={m.sender_picture} alt="" className="w-8 h-8 rounded-sm object-cover border border-white/10 shrink-0" />
                ) : (
                  <div className="w-8 h-8 bg-primary/10 border border-primary/30 text-primary flex items-center justify-center font-mono text-[10px] rounded-sm shrink-0">
                    {m.sender_name?.[0]?.toUpperCase()}
                  </div>
                )}
                <div className={`max-w-[70%] ${mine ? "text-right" : ""}`}>
                  <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/50">
                    {m.sender_name} · {formatTime(m.created_at)}
                  </div>
                  <div
                    className={`mt-1 px-3 py-2 rounded-sm text-sm whitespace-pre-wrap break-words ${
                      mine ? "bg-primary/15 border border-primary/30 text-white" : "bg-white/5 border border-white/10 text-white/90"
                    }`}
                  >
                    {m.content}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <form onSubmit={send} className="border-t border-white/10 p-3 flex gap-2">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Nachricht schreiben…"
            className="input flex-1"
            data-testid="chat-input"
            disabled={!activeId}
          />
          <button type="submit" className="btn-primary" disabled={!text.trim() || !activeId} data-testid="chat-send-btn">
            <Send className="w-4 h-4" />
          </button>
        </form>
        {error && <div className="px-4 py-2 text-xs text-red-400 border-t border-red-400/30 font-mono">{error}</div>}
      </section>
    </div>
  );
}
